import { Photo } from "@/core/photo"
import { IOAdapter, IOAdapterOptions, type PreparedIOAdapterImport } from "@/core/io-adapter"
import { RawInfo } from "@/lib/raw/fastlib"
import { vec2, vec3, vec4 } from "gl-matrix"
import { getDefaultAdjustments } from "@/core/adjustments"
import {
	cleanupWaveletTextures,
	denoiseTextureWaveletChunked,
	initDenoiseTextures,
} from "@/core/utils/denoise"
import { Texture } from "@/core/backends/webgl/texture"
import { Adjustments, ColorProfileName } from "@/core/types"
import { hasExtension } from "@/storage/file-system"
import { Renderer } from "@/core/pipeline/renderer"
import { PeerConnection } from "@/utils/peer"
import type { RawLoaderWorker } from "@/lib/raw/raw-loader.worker"
import { PhotoEmbeddedProfile } from "@/core/profile-manager"
import { DNGColorWhiteBalance } from "./white-balance/dng-white-balance"
import { GenericWhiteBalance } from "./white-balance/generic-white-balance"
import { RAWColorWhiteBalance } from "./white-balance/raw-white-balance"
import { getCameraProfileId, loadCameraProfile } from "@/lib/raw/camera-utils"
import { getLensProfile } from "@/core/utils/lens/lens-profile"
import { BayerRCDDemosaicer } from "./demosaic/bayer-rcd-demosaicer"
import { FujiXTransDemosaicer } from "./demosaic/fuji-x-trans-demosaicer"
import { PassthroughDemosaicer } from "./demosaic/passthrough-demosaicer"
import { RawDemosaicer } from "./demosaic/raw-demosaicer"
import { HighlightsReconstruction } from "./highlights-reconstruction"
import { DenoiseTextures } from "@/core/pipeline/passes/wavelet-denoise-pass"
import {
	blitChunkTextureIntoFinalTexture,
	blitDenoiseChunkTexturesIntoFinalTextures,
	CHUNK_PADDING,
	CHUNK_SIZE,
} from "./demosaic/chunking-utils"
import { getCameraAdjustments } from "./camera-default-adjustments"
import {
	commitTrackingPerformanceValue,
	setPerformanceStat,
	startTrackingPerformanceValue,
} from "@/utils/performance-stats"
import { isSourceMonochromeRaw } from "./raw-info"
import { rawAdapterExtensions } from "./raw-adapter-extensions"

interface RawIOAdapterOptions extends IOAdapterOptions {
	adjustments: Adjustments
}

export class RawIOAdapter extends IOAdapter {
	public static supportedExtensions = rawAdapterExtensions

	private rawConnection: PeerConnection<RawLoaderWorker>

	private highlightsReconstruction = new HighlightsReconstruction()
	private bayerDemosaicer = new BayerRCDDemosaicer()
	private fujiDemosaicer = new FujiXTransDemosaicer()
	private passthroughDemosaicer = new PassthroughDemosaicer()

	// Legacy
	// private demosaicRcdPass?: LegacyDemosaicRcdPass
	// private demosaicXtransPass?: LegacyDemosaicXtransPass
	// private highlightsReconPass?: LegacyHighlightsReconPass

	public constructor(connections: { rawConnection: PeerConnection<RawLoaderWorker> }) {
		super()
		this.rawConnection = connections.rawConnection
	}

	public getName(): string {
		return "raw-adapter"
	}

	public canLoad(file: File): boolean {
		return hasExtension(file, RawIOAdapter.supportedExtensions)
	}

	public async load(file: File, renderer: Renderer, options: IOAdapterOptions): Promise<void> {
		const preparedImport = this.consumePreparedImport(file, renderer, options)
		if (preparedImport) {
			await preparedImport
			return
		}

		renderer.performanceCounters.beginLogicalBlock(`load-raw-pipeline`)

		/**
		 * Load metadata
		 */

		const rawLoader = this.rawConnection.proxy

		if (!rawLoader) {
			console.warn("RAW IO adapter requires RAW workers")
			return
		}

		/**
		 * Set cancellation listeners
		 */

		options.signal?.throwIfAborted()

		options.signal?.addEventListener("abort", () => {
			console.log(`[io-raw:${renderer.label}] Load canceled`, options.photoId)
			rawLoader.cancelLoading()
		})

		/**
		 * Load and decode RAW
		 */

		console.log(`[io-raw:${renderer.label}] Start loading raw`, options.photoId)

		// Per-photo timing — start/commit pair for the worker round-trip
		// (decode runs in the worker; the wall-clock here is "approximate"
		// in the sense that it includes message-port latency, which the
		// worker-side `info.decodeTime` does not).
		const photoId = options.photoId ?? ""
		startTrackingPerformanceValue(photoId, "rawLoad")
		let info
		try {
			info = await rawLoader.loadRaw(file, { metadata: options.metadata })
			setPerformanceStat(photoId, "rawLoadStatus", "ok")
			setPerformanceStat(photoId, "rawBytesSize", info.raw_data_length ?? 0)
		} catch (e) {
			setPerformanceStat(
				photoId,
				"rawLoadStatus",
				options.signal?.aborted ? "cancelled" : (e as Error).message || "error",
			)
			throw e
		} finally {
			commitTrackingPerformanceValue(photoId, "rawLoad")
		}

		console.log(
			`[io-raw:${renderer.label}] Loaded raw`,
			options.photoId,
			"in",
			`${info.decodeTime ?? 0}ms`,
		)

		// Real texture dimensions = the cropped RAW dimensions used to
		// allocate the source/input textures. EXIF width/height can lie
		// (rotated, mismatched crop), so this is the source of truth.
		const dimensions = this.getCroppedDimensions(info)
		setPerformanceStat(photoId, "width", dimensions.width)
		setPerformanceStat(photoId, "height", dimensions.height)

		renderer.performanceCounters.add("decodeSourceFile", info.decodeTime ?? 0, 0)

		options.signal?.throwIfAborted()

		/**
		 * Set default adjustments
		 */

		const adjustments = options.adjustments ?? getDefaultAdjustments(file)

		/**
		 * Load camera & lens profile
		 */

		const cameraID = getCameraProfileId(info, options.metadata)

		if (!info.skip_profile) {
			info.profile = await loadCameraProfile(cameraID)

			if (info.profile) {
				console.log(`[RawLoader]: Camera profile loaded (${cameraID})`, info.profile)
			}

			if (info.profile && info.profile_name === "Embedded") {
				info.profile.hueSatMap1 = undefined
				info.profile.hueSatMap2 = undefined
			}
		}

		info.lensProfile = await getLensProfile(info, options.metadata)

		if (info.lensProfile) {
			console.log("[RawLoader]: Lens profile loaded", info.lensProfile)
		}

		options.signal?.throwIfAborted()

		/**
		 * Rendering raw file
		 */

		console.log(`[io-raw:${renderer.label}] Rendering RAW`, options.photoId)

		startTrackingPerformanceValue(photoId, "rawRender")
		try {
			const result = this.loadRawData(renderer, info, {
				...options,
				enableHalfResolution: options.enableHalfResolution,
				adjustments,
			})

			if (!options.signal?.aborted) {
				renderer.denoiseEnabled = result.denoiseEnabled

				renderer.photo = result.photo

				renderer.profileManager.setProfile(
					renderer.photo,
					info.profile,
					info.profile_tone_curve,
				)
				renderer.profileManager.setLensProfile(info.lensProfile)
				renderer.profileManager.setEmbeddedProfile(result.embeddedProfile ?? null)
			}
		} finally {
			commitTrackingPerformanceValue(photoId, "rawRender")
		}

		renderer.performanceCounters.endLogicalBlock(`load-raw-pipeline`)
	}

	public async prepare(file: File, options: IOAdapterOptions): Promise<PreparedIOAdapterImport> {
		return this.prepareInternal(file, options)
	}

	private async prepareInternal(
		file: File,
		options: IOAdapterOptions,
	): Promise<PreparedIOAdapterImport> {
		const rawLoader = this.rawConnection.proxy

		if (!rawLoader) {
			throw new Error("RAW IO adapter requires RAW workers")
		}

		const photoId = options.photoId ?? ""
		let leaseId: number | undefined
		let disposePromise: Promise<void> | undefined
		const dispose = async () => {
			if (leaseId === undefined) {
				return
			}

			disposePromise ??= (async () => {
				await rawLoader.releasePreparedRaw(leaseId)
			})()
			await disposePromise
		}
		const cancelLoading = () => {
			void rawLoader.cancelLoading().catch(() => void 0)
		}

		try {
			options.signal?.throwIfAborted()
			options.signal?.addEventListener("abort", cancelLoading, { once: true })

			let info: RawInfo
			try {
				// This round-trip includes worker message latency in addition to decode time.
				startTrackingPerformanceValue(photoId, "rawLoad")
				try {
					const preparedRaw = await rawLoader.prepareRaw(file, {
						metadata: options.metadata,
					})
					leaseId = preparedRaw.leaseId
					info = preparedRaw.info
					setPerformanceStat(photoId, "rawLoadStatus", "ok")
					setPerformanceStat(photoId, "rawBytesSize", info.raw_data_length ?? 0)
				} catch (e) {
					setPerformanceStat(
						photoId,
						"rawLoadStatus",
						options.signal?.aborted
							? "cancelled"
							: e instanceof Error
								? e.message || "error"
								: "error",
					)
					throw e
				} finally {
					commitTrackingPerformanceValue(photoId, "rawLoad")
				}
			} finally {
				options.signal?.removeEventListener("abort", cancelLoading)
			}

			// EXIF dimensions can disagree with the cropped RAW dimensions.
			const dimensions = this.getCroppedDimensions(info)
			setPerformanceStat(photoId, "width", dimensions.width)
			setPerformanceStat(photoId, "height", dimensions.height)

			options.signal?.throwIfAborted()

			const adjustments = options.adjustments ?? getDefaultAdjustments(file)
			const cameraID = getCameraProfileId(info, options.metadata)

			if (!info.skip_profile) {
				info.profile = await loadCameraProfile(cameraID)

				if (info.profile && info.profile_name === "Embedded") {
					info.profile.hueSatMap1 = undefined
					info.profile.hueSatMap2 = undefined
				}
			}

			info.lensProfile = await getLensProfile(info, options.metadata)

			options.signal?.throwIfAborted()

			const commitOptions: RawIOAdapterOptions = {
				...options,
				enableHalfResolution: options.enableHalfResolution,
				adjustments,
			}

			return this.createPreparedImport(
				file,
				commitOptions,
				(renderer, preparedOptions) =>
					this.commitPreparedRaw(renderer, info, preparedOptions as RawIOAdapterOptions),
				dispose,
			)
		} catch (error) {
			await dispose()
			throw error
		}
	}

	private commitPreparedRaw(renderer: Renderer, info: RawInfo, options: RawIOAdapterOptions) {
		renderer.performanceCounters.beginLogicalBlock(`load-raw-pipeline`)
		try {
			renderer.performanceCounters.add("decodeSourceFile", info.decodeTime ?? 0, 0)
			options.signal?.throwIfAborted()
			const photoId = options.photoId ?? ""
			startTrackingPerformanceValue(photoId, "rawRender")
			try {
				const result = this.loadRawData(renderer, info, options)

				if (!options.signal?.aborted) {
					renderer.denoiseEnabled = result.denoiseEnabled

					renderer.photo = result.photo

					renderer.profileManager.setProfile(
						renderer.photo,
						info.profile,
						info.profile_tone_curve,
					)
					renderer.profileManager.setLensProfile(info.lensProfile)
					renderer.profileManager.setEmbeddedProfile(result.embeddedProfile ?? null)
				}
			} finally {
				commitTrackingPerformanceValue(photoId, "rawRender")
			}
		} finally {
			renderer.performanceCounters.endLogicalBlock(`load-raw-pipeline`)
		}
	}

	public loadRawData(renderer: Renderer, info: RawInfo, options: RawIOAdapterOptions) {
		const denoiseEnabled = options.enableDenoise ?? true
		const adjustments = options.adjustments

		const dimensions = this.getCroppedDimensions(info)
		const orientation = this.getOrientation(info)
		const embeddedProfile = this.getEmbeddedProfile(info, options)
		let cameraProfileExposureBias = 0

		const cameraAdjustments = getCameraAdjustments(info.camera_maker, info.camera_model)

		if (cameraAdjustments) {
			if (cameraAdjustments.exposure) {
				cameraProfileExposureBias = cameraAdjustments.exposure
			}
		}

		const rawTexture = this.createRawTexture(renderer, info)

		renderer.performanceCounters.begin("render-raw-texture")

		const result = this.renderRawTexture(renderer, info, rawTexture, options)

		renderer.performanceCounters.end("render-raw-texture")

		if (embeddedProfile) {
			adjustments.colorProfile = ColorProfileName.Embedded
		}

		return {
			denoiseEnabled,
			photo: new Photo({
				id: options.photoId,
				type: "raw",
				texture: result.texture,
				whiteBalance: result.whiteBalance,
				adjustments,
				orientation: orientation[0],
				exposureBias: cameraProfileExposureBias,
				originalImageSize: [dimensions.width, dimensions.height],
				isSourceMonochrome: isSourceMonochromeRaw(info),
				// TODO: support orientation flip
			}),
			embeddedProfile,
		}
	}

	private createRawTexture(renderer: Renderer, info: RawInfo): Texture {
		const is32 = info.bytes_per_pixel === 12
		const isMono = isSourceMonochromeRaw(info)

		const data = is32
			? new Uint32Array(info.raw_data, info.raw_data_offset, info.raw_data_length >> 2)
			: new Uint16Array(info.raw_data, info.raw_data_offset, info.raw_data_length >> 1)

		const cropped = this.getCroppedDimensions(info)

		const padding = info.is_cfa
			? info.padding
			: info.raw_data_length % (info.pitch_in_bytes + info.padding) === 0
				? info.padding
				: 0
		const bytesPerRow = info.pitch_in_bytes + padding
		const rowLength = bytesPerRow / info.bytes_per_pixel
		const skipRows = cropped.y

		const height = cropped.height
		const width = cropped.width + cropped.x

		if (!info.is_cfa) {
			renderer.sourceTexture.update({
				format: is32 ? "rgb32uint" : isMono ? "r16uint" : "rgb16uint",
				width,
				height,
				data,
				skipRows,
				skipCols: 0,
				rowLength,
			})
		} else {
			renderer.sourceTexture.update({
				format: info.components_per_pixel === 2 ? "rg16uint" : "r16uint",
				width: width,
				height: height,
				data,
				skipRows,
				skipCols: 0,
				rowLength,
			})
		}

		return renderer.sourceTexture
	}

	private renderRawTexture(
		renderer: Renderer,
		info: RawInfo,
		rawTexture: Texture,
		options: RawIOAdapterOptions,
	) {
		const levels = this.getBlackWhiteLevels(info)
		const cropped = this.getCroppedDimensions(info)
		const embeddedProfile = this.getEmbeddedProfile(info, options)

		let shouldUseDNGColorWhiteBalance = false

		if (embeddedProfile || info.profile_name === "Embedded") {
			shouldUseDNGColorWhiteBalance = true
		}

		const wb: GenericWhiteBalance = shouldUseDNGColorWhiteBalance
			? new DNGColorWhiteBalance(info)
			: new RAWColorWhiteBalance(info)

		const camToXYZ = wb.camToPCS
		const whiteBalanceMultipliers = wb.whiteBalanceMultipliers
		const temperature = wb.whiteBalance.temperature
		const tint = wb.whiteBalance.tint

		const isHalfSize = (options.enableHalfResolution ?? false) && info.is_cfa
		const isXTrans = info.cfa_height === 6 && info.cfa_width === 6

		let outputWidth = cropped.width
		let outputHeight = cropped.height
		let scale = 1.0

		if (isHalfSize) {
			scale = isXTrans ? 3 : 2
			outputWidth = Math.floor(outputWidth / scale)
			outputHeight = Math.floor(outputHeight / scale)
		}

		// inputTexture is 16bit/32bit float, ironically this is our output texture
		renderer.inputTexture.update({
			format: renderer.backend.textureFormat,
			data: null,
			width: outputWidth,
			height: outputHeight,
		})

		const outputTexture = renderer.inputTexture

		const demosaicer: RawDemosaicer = !info.is_cfa
			? this.passthroughDemosaicer
			: isXTrans
				? this.fujiDemosaicer
				: this.bayerDemosaicer

		const gl = renderer.backend.gl
		const globalOffsetX = cropped.x / scale || 0
		const globalOffsetY = 0

		// Preparing chunk textures

		const totalChunkSize = CHUNK_SIZE + 2 * CHUNK_PADDING

		const chunkDenoiseTextures = renderer.chunkDenoiseTextures

		let outputDenoiseTextures: DenoiseTextures | null = null

		// Chunked denoise textures

		if (options.enableDenoise) {
			outputDenoiseTextures = initDenoiseTextures(renderer)

			outputDenoiseTextures.denoiseLumTexture.updateSize(outputWidth, outputHeight)
			outputDenoiseTextures.denoiseColorTexture.updateSize(
				Math.floor((outputWidth + 1) / 2),
				Math.floor((outputHeight + 1) / 2),
			)

			chunkDenoiseTextures.denoiseLumTexture.updateSize(totalChunkSize, totalChunkSize)
			chunkDenoiseTextures.denoiseColorTexture.updateSize(
				totalChunkSize / 2.0,
				totalChunkSize / 2.0,
			)
		}

		// Chunked color texture

		const buffers = renderer.chunkingBuffers
		buffers.updateSize(totalChunkSize, totalChunkSize)

		// Framebuffers

		const fboRead = gl.createFramebuffer()
		const fboDraw = gl.createFramebuffer()

		if (!fboRead || !fboDraw) {
			throw new Error("Cannot demosaic Bayer pattern photos due to internal error.")
		}

		gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, fboDraw)
		gl.framebufferTexture2D(
			gl.DRAW_FRAMEBUFFER,
			gl.COLOR_ATTACHMENT0,
			gl.TEXTURE_2D,
			outputTexture.nativeTexture,
			0,
		)

		gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)

		const nX = Math.ceil(outputWidth / CHUNK_SIZE)
		const nY = Math.ceil(outputHeight / CHUNK_SIZE)

		// renderer.performanceCounters.begin(
		// 	`tiledDemosaicHighlightsDenoise-${outputWidth}x${outputHeight}-tx${nX}-ty${nY}-ts${this.CHUNK_SIZE}`,
		// )

		for (let yi = 0; yi < nY; yi++) {
			for (let xi = 0; xi < nX; xi++) {
				const x0 = xi * CHUNK_SIZE,
					y0 = yi * CHUNK_SIZE
				const x0r = Math.floor((globalOffsetX + x0 - CHUNK_PADDING) * scale)
				const y0r = Math.floor((globalOffsetY + y0 - CHUNK_PADDING) * scale)

				// Demosaicing chunk

				demosaicer.demosaic(rawTexture, buffers, renderer, {
					isHalfSize,
					globalOffset: [x0r, y0r],
					outputWidth: totalChunkSize,
					outputHeight: totalChunkSize,
					whiteBalanceMultipliers,
					levels,
					cfaArray: info.is_cfa ? (Array.from(info.cfa_array) as vec4) : [0, 0, 0, 0],
					info,
					croppedSize: cropped,
					highlightsReconstruction: this.highlightsReconstruction,
				})

				// Highlights reconstruction

				this.highlightsReconstruction.render(
					buffers.writeBuffer,
					buffers.readBuffer,
					renderer,
					{
						wbCoeffs: whiteBalanceMultipliers,
						camToXYZ,
						computeChrominan: false,
					},
				)

				// Denoise if enabled

				if (options.enableDenoise && outputDenoiseTextures) {
					denoiseTextureWaveletChunked(
						renderer,
						buffers.readBuffer,
						chunkDenoiseTextures,
						{
							iso: options.metadata?.iso ?? 400,
							shutterSpeed: options.metadata?.shutterSpeed ?? 0.01,
							isJpeg: false,
						},
					)

					blitDenoiseChunkTexturesIntoFinalTextures(
						gl,
						fboDraw,
						fboRead,
						chunkDenoiseTextures,
						outputDenoiseTextures,
						[x0, y0],
					)
				}

				// Rendering demosaiced chunk onto the final large texure

				blitChunkTextureIntoFinalTexture(
					gl,
					fboDraw,
					fboRead,
					buffers.readBuffer,
					outputTexture,
					[x0, y0],
				)
			}
		}

		// Cleanup

		gl.deleteFramebuffer(fboRead)
		gl.deleteFramebuffer(fboDraw)

		renderer.inputTexture.generateMipmap()

		renderer.sourceTexture.unbind(0)
		renderer.sourceTexture.reset()

		renderer.dualBuffers.reset()
		renderer.originalDualBuffers.reset()
		renderer.maskBuffers.reset()
		renderer.blurBuffers.reset()

		cleanupWaveletTextures(renderer)

		return {
			texture: renderer.inputTexture,
			whiteBalance: {
				asShotTemperature: temperature,
				asShotTint: tint,
			},
		}
	}

	private getBlackWhiteLevels(info: RawInfo): vec2 {
		const whitePoint = info.white_point
		let blackLevel = info.black_level

		// Canon EOS R6m2 camera seems to have wrong blackLevel, but raw_black_level_separate is correct.
		if (blackLevel < 0 || blackLevel !== (info.raw_black_level_separate[0] || blackLevel)) {
			blackLevel =
				(<number>info.raw_black_level_separate[0] +
					<number>info.raw_black_level_separate[1] +
					<number>info.raw_black_level_separate[2] +
					<number>info.raw_black_level_separate[3]) /
				4
		}

		return [blackLevel, whitePoint]
	}

	/* Utils */

	private getCroppedDimensions(info: RawInfo) {
		let { crop_x, crop_y, crop_width, crop_height } = info

		// Extra padding to crop off debayered artifacts at border
		const padding = 0

		// Set minimum crop to prevent bayer artifacts from showing around the edges
		crop_x = crop_x + padding
		crop_y = crop_y + padding
		crop_width = crop_width - padding
		crop_height = crop_height - padding

		return {
			x: crop_x,
			y: crop_y,
			width: crop_width,
			height: crop_height,
		}
	}

	private getEmbeddedProfile(
		info: RawInfo,
		options: RawIOAdapterOptions,
	): PhotoEmbeddedProfile | undefined {
		if (info.profile_name !== "Apple Embedded Color Profile") {
			return
		}

		let baselineExposure = (options.metadata?.auxiliaryData?.baselineExposure as number) ?? 0.0

		if (info.profile_gain_table_map && options.metadata?.fNumber) {
			// TODO: @din figure out why exactly it is max 1.9.
			baselineExposure -= Math.min(options.metadata?.fNumber ?? 0.0, 1.9)
		}

		return {
			name: info.profile_name,
			baselineExposure,
			gainTableMap: info.profile_gain_table_map,
		}
	}

	private getOrientation(info: RawInfo): vec3 {
		// [rotation, flip x, flip y]
		switch (info.orientation) {
			case 1:
				return [0, 1, 1]
			case 2:
				return [0, -1, 1]
			case 3:
				return [2, 1, 1]
			case 4:
				return [0, 1, -1]
			case 5:
				return [1, -1, 1]
			case 6:
				return [1, 1, 1]
			case 7:
				return [-1, -1, 1]
			case 8:
				return [-1, 1, 1]
			default:
				return [0, 1, 1]
		}
	}
}
